import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Zap, Moon, Sun, Calendar, Tag, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import QuickCompress from './QuickCompress';
import App from '../App';

// Render nested component inside columns
function RenderNestedComponent({ component }) {
  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right'
  };
  
  switch (component.type) {
    case 'heading':
      const HeadingTag = component.content.level || 'h3';
      const nestedHeadingClasses = {
        h2: 'text-2xl md:text-3xl font-bold',
        h3: 'text-xl md:text-2xl font-semibold',
        h4: 'text-lg md:text-xl font-semibold'
      };
      const nestedHeadingStyle = component.content.color ? { color: component.content.color } : {};
      return (
        <HeadingTag 
          className={`${nestedHeadingClasses[HeadingTag] || nestedHeadingClasses.h3} ${alignClasses[component.content.align] || ''} mb-3`}
          style={nestedHeadingStyle}
        >
          {component.content.text}
        </HeadingTag>
      );
      
    case 'text':
      const nestedTextStyle = component.content.color ? { color: component.content.color } : {};
      return (
        <p 
          className={`leading-relaxed mb-3 whitespace-pre-wrap ${alignClasses[component.content.align] || ''}`}
          style={nestedTextStyle}
        >
          {component.content.text}
        </p>
      );
      
    case 'image':
      return (
        <div className="mb-3">
          <img
            src={component.content.src}
            alt={component.content.alt || ''}
            className="w-full rounded-lg"
          />
        </div>
      );
      
    case 'button':
      const nestedBtnSizes = {
        xs: 'px-2.5 py-1 text-xs',
        sm: 'px-3.5 py-1.5 text-sm',
        md: 'px-5 py-2.5 text-sm',
        lg: 'px-6 py-3 text-base',
        xl: 'px-8 py-4 text-lg'
      };
      const nestedBtnRounded = {
        none: 'rounded-none',
        sm: 'rounded',
        md: 'rounded-md',
        lg: 'rounded-lg',
        full: 'rounded-full'
      };
      const nestedBtnStyle = {
        backgroundColor: component.content.bgColor || '#2563eb',
        color: component.content.textColor || '#ffffff'
      };
      return (
        <div className="mb-3">
          <a
            href={component.content.url || '#'}
            className={`inline-block ${nestedBtnSizes[component.content.size] || nestedBtnSizes.md} ${nestedBtnRounded[component.content.rounded] || nestedBtnRounded.full} font-medium transition-opacity hover:opacity-90`}
            style={nestedBtnStyle}
          >
            {component.content.text || 'Button'}
          </a>
        </div>
      );
      
    default:
      return null;
  }
}

// Component renderer for CMS components
function RenderComponent({ component }) {
  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right'
  };

  switch (component.type) {
    case 'heading':
      const HeadingTag = component.content.level || 'h2';
      const headingClasses = {
        h1: 'text-4xl md:text-5xl font-bold',
        h2: 'text-3xl md:text-4xl font-bold',
        h3: 'text-2xl md:text-3xl font-semibold',
        h4: 'text-xl md:text-2xl font-semibold'
      };
      const headingAlign = alignClasses[component.content.align] || '';
      const headingStyle = component.content.color ? { color: component.content.color } : {};
      return (
        <HeadingTag 
          className={`${headingClasses[HeadingTag]} ${headingAlign} mb-4`}
          style={headingStyle}
        >
          {component.content.text}
        </HeadingTag>
      );

    case 'text':
      const textAlign = alignClasses[component.content.align] || '';
      const textStyle = component.content.color ? { color: component.content.color } : {};
      return (
        <p 
          className={`leading-relaxed mb-4 whitespace-pre-wrap ${textAlign}`}
          style={textStyle}
        >
          {component.content.text}
        </p>
      );

    case 'image':
      const imgWidths = {
        'full': 'w-full',
        '3/4': 'w-3/4',
        '1/2': 'w-1/2',
        '1/3': 'w-1/3'
      };
      return (
        <div className="mb-4">
          <img
            src={component.content.src}
            alt={component.content.alt || ''}
            className={`${imgWidths[component.content.width] || 'w-full'} rounded-lg`}
          />
        </div>
      );

    case 'button':
      const btnAlign = component.content.align || 'left';
      const btnAlignClass = btnAlign === 'center' ? 'text-center' : btnAlign === 'right' ? 'text-right' : 'text-left';
      const btnSizes = {
        xs: 'px-3 py-1.5 text-xs',
        sm: 'px-4 py-2 text-sm',
        md: 'px-6 py-3 text-base',
        lg: 'px-8 py-4 text-lg',
        xl: 'px-10 py-5 text-xl'
      };
      const btnRounded = {
        none: 'rounded-none',
        sm: 'rounded',
        md: 'rounded-md',
        lg: 'rounded-lg',
        full: 'rounded-full'
      };
      const btnMarginTop = {
        none: '',
        sm: 'mt-2',
        md: 'mt-4',
        lg: 'mt-8'
      };
      const btnStyle = {
        backgroundColor: component.content.bgColor || '#2563eb',
        color: component.content.textColor || '#ffffff'
      };
      return (
        <div className={`mb-4 ${btnAlignClass} ${btnMarginTop[component.content.marginTop] || ''}`}>
          <a
            href={component.content.url || '#'}
            className={`inline-block ${btnSizes[component.content.size] || btnSizes.md} ${btnRounded[component.content.rounded] || btnRounded.full} font-medium transition-opacity hover:opacity-90`}
            style={btnStyle}
          >
            {component.content.text || 'Button'}
          </a>
        </div>
      );

    case 'divider':
      const dividerStyles = {
        solid: 'border-solid',
        dashed: 'border-dashed',
        dotted: 'border-dotted'
      };
      const dividerWidths = {
        'full': 'w-full',
        '3/4': 'w-3/4 mx-auto',
        '1/2': 'w-1/2 mx-auto'
      };
      return (
        <hr className={`border-slate-200 dark:border-slate-700 my-6 ${dividerStyles[component.content.style] || ''} ${dividerWidths[component.content.width] || 'w-full'}`} />
      );

    case 'spacer':
      const spacerSizes = { 
        xs: 'h-2', 
        sm: 'h-4', 
        md: 'h-8', 
        lg: 'h-16', 
        xl: 'h-24',
        '2xl': 'h-32'
      };
      return <div className={spacerSizes[component.content.size] || 'h-8'} />;

    case 'section':
      const sectionBgs = {
        none: '',
        light: 'bg-slate-100 dark:bg-slate-800',
        dark: 'bg-slate-800 dark:bg-slate-900',
        primary: 'bg-blue-600 text-white',
        gradient: 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
      };
      const sectionPadding = {
        none: '',
        sm: 'py-4',
        md: 'py-8',
        lg: 'py-16',
        xl: 'py-24'
      };
      const sectionBg = component.content.bgType === 'custom' && component.content.bgColor 
        ? { backgroundColor: component.content.bgColor }
        : {};
      const sectionChildren = component.content.children || [];
      return (
        <section 
          className={`${sectionBgs[component.content.bgType] || ''} ${sectionPadding[component.content.padding] || 'py-8'} -mx-4 px-4 my-4`}
          style={sectionBg}
        >
          <div className="container mx-auto max-w-4xl">
            {sectionChildren.map((child, idx) => (
              <RenderNestedComponent key={child.id || idx} component={child} />
            ))}
          </div>
        </section>
      );

    case 'container':
      const containerWidths = {
        sm: 'max-w-xl',
        md: 'max-w-2xl',
        lg: 'max-w-4xl',
        xl: 'max-w-6xl',
        full: 'max-w-full'
      };
      const containerPadding = {
        none: '',
        sm: 'p-4',
        md: 'p-6',
        lg: 'p-8'
      };
      const containerBgs = {
        none: '',
        white: 'bg-white dark:bg-slate-800',
        light: 'bg-slate-50 dark:bg-slate-800',
        dark: 'bg-slate-800 dark:bg-slate-900'
      };
      const containerRounded = {
        none: '',
        sm: 'rounded',
        md: 'rounded-lg',
        lg: 'rounded-xl'
      };
      return (
        <div className={`mx-auto my-4 ${containerWidths[component.content.maxWidth] || 'max-w-4xl'} ${containerPadding[component.content.padding] || ''} ${containerBgs[component.content.bgType] || ''} ${containerRounded[component.content.rounded] || ''}`}>
          {/* Container wrapper */}
        </div>
      );

    case 'columns':
      const columnLayouts = {
        '2-equal': 'grid-cols-1 md:grid-cols-2',
        '3-equal': 'grid-cols-1 md:grid-cols-3',
        '4-equal': 'grid-cols-1 md:grid-cols-4',
        '1-2': 'grid-cols-1 md:grid-cols-3',
        '2-1': 'grid-cols-1 md:grid-cols-3',
        '1-1-2': 'grid-cols-1 md:grid-cols-4'
      };
      const columnGaps = {
        none: 'gap-0',
        sm: 'gap-2',
        md: 'gap-4',
        lg: 'gap-8'
      };
      
      const getColSpanClass = (layout, idx) => {
        if (layout === '1-2') return idx === 0 ? 'md:col-span-1' : 'md:col-span-2';
        if (layout === '2-1') return idx === 0 ? 'md:col-span-2' : 'md:col-span-1';
        if (layout === '1-1-2') return idx === 2 ? 'md:col-span-2' : 'md:col-span-1';
        return '';
      };
      
      const columns = component.content.columns || [];
      
      return (
        <div className={`grid ${columnLayouts[component.content.layout] || 'grid-cols-2'} ${columnGaps[component.content.gap] || 'gap-4'} my-4`}>
          {columns.map((col, idx) => (
            <div 
              key={col.id || idx} 
              className={`${getColSpanClass(component.content.layout, idx)} min-h-[50px]`}
            >
              {(col.components || []).map((nestedComp, nestedIdx) => (
                <RenderNestedComponent key={nestedComp.id || nestedIdx} component={nestedComp} />
              ))}
            </div>
          ))}
        </div>
      );

    case 'html':
      return (
        <div
          className="mb-4"
          dangerouslySetInnerHTML={{ __html: component.content.html || '' }}
        />
      );

    case 'tool':
      if (component.content.tool === 'quick-50kb') {
        return (
          <div className="my-8">
            <QuickCompress embedded={true} />
          </div>
        );
      } else if (component.content.tool === 'full-compressor') {
        return (
          <div className="my-8">
            <App embedded={true} />
          </div>
        );
      }
      return null;

    case 'ad':
      const adAlign = {
        left: 'text-left',
        center: 'text-center',
        right: 'text-right'
      };
      const adFormats = {
        auto: { width: '100%', height: 'auto' },
        horizontal: { width: '728px', height: '90px' },
        vertical: { width: '160px', height: '600px' },
        rectangle: { width: '336px', height: '280px' },
        'in-article': { width: '100%', height: 'auto' },
        'in-feed': { width: '100%', height: 'auto' }
      };
      const adFormat = adFormats[component.content.format] || adFormats.auto;
      
      return (
        <div className={`my-4 ${adAlign[component.content.align] || 'text-center'}`}>
          <ins
            className="adsbygoogle"
            style={{
              display: 'block',
              maxWidth: adFormat.width,
              height: adFormat.height !== 'auto' ? adFormat.height : undefined,
              margin: component.content.align === 'center' ? '0 auto' : undefined
            }}
            data-ad-client={window.ADSENSE_PUBLISHER_ID || ''}
            data-ad-slot={component.content.slotId || ''}
            data-ad-format={component.content.format === 'auto' ? 'auto' : 'rectangle'}
            data-full-width-responsive={component.content.format === 'auto' ? 'true' : 'false'}
          />
        </div>
      );

    default:
      return null;
  }
}

// Sidebar Widget Renderer
function RenderSidebarWidget({ widget, posts, categories, onSearch }) {
  switch (widget.type) {
    case 'heading':
      const SidebarHeadingTag = widget.content.level || 'h3';
      const sidebarHeadingClasses = {
        h2: 'text-xl font-bold mb-3',
        h3: 'text-lg font-semibold mb-2',
        h4: 'text-base font-semibold mb-2'
      };
      return (
        <SidebarHeadingTag className={`${sidebarHeadingClasses[SidebarHeadingTag] || sidebarHeadingClasses.h3} text-slate-900 dark:text-white`}>
          {widget.content.text}
        </SidebarHeadingTag>
      );

    case 'text':
      return (
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 whitespace-pre-wrap leading-relaxed">
          {widget.content.text}
        </p>
      );

    case 'image':
      const imgContent = widget.content.link ? (
        <a href={widget.content.link} className="block">
          <img
            src={widget.content.src}
            alt={widget.content.alt || ''}
            className="w-full rounded-lg hover:opacity-90 transition-opacity"
          />
        </a>
      ) : (
        <img
          src={widget.content.src}
          alt={widget.content.alt || ''}
          className="w-full rounded-lg"
        />
      );
      return <div className="mb-4">{imgContent}</div>;

    case 'button':
      const btnStyle = {
        backgroundColor: widget.content.bgColor || '#2563eb',
        color: widget.content.textColor || '#ffffff'
      };
      return (
        <div className="mb-4">
          <a
            href={widget.content.url || '#'}
            className="inline-block w-full text-center px-4 py-2 rounded-lg font-medium transition-opacity hover:opacity-90"
            style={btnStyle}
          >
            {widget.content.text || 'Click Here'}
          </a>
        </div>
      );

    case 'html':
      return (
        <div
          className="mb-4 sidebar-html"
          dangerouslySetInnerHTML={{ __html: widget.content.html || '' }}
        />
      );

    case 'search':
      return (
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder={widget.content.placeholder || 'Search...'}
              className="pl-10 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && onSearch) {
                  onSearch(e.target.value);
                }
              }}
            />
          </div>
        </div>
      );

    case 'categories':
      return (
        <div className="mb-4">
          {widget.content.title && (
            <h4 className="text-base font-semibold text-slate-900 dark:text-white mb-3">
              {widget.content.title}
            </h4>
          )}
          <ul className="space-y-2">
            {categories && categories.length > 0 ? (
              categories.map((cat, idx) => (
                <li key={idx}>
                  <a
                    href={`/category/${cat.slug || cat.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-sm text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                  >
                    {cat.name} {cat.count && <span className="text-slate-400">({cat.count})</span>}
                  </a>
                </li>
              ))
            ) : (
              <li className="text-sm text-slate-500">No categories yet</li>
            )}
          </ul>
        </div>
      );

    case 'recent-posts':
      const postCount = parseInt(widget.content.count || '5');
      const recentPosts = posts ? posts.slice(0, postCount) : [];
      return (
        <div className="mb-4">
          {widget.content.title && (
            <h4 className="text-base font-semibold text-slate-900 dark:text-white mb-3">
              {widget.content.title}
            </h4>
          )}
          <ul className="space-y-3">
            {recentPosts.length > 0 ? (
              recentPosts.map((post, idx) => (
                <li key={post.id || idx}>
                  <Link
                    to={`/${post.slug}`}
                    className="block group"
                  >
                    <span className="text-sm text-slate-700 dark:text-slate-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-2">
                      {post.title}
                    </span>
                    {post.created_at && (
                      <span className="text-xs text-slate-400 mt-0.5 block">
                        {new Date(post.created_at).toLocaleDateString()}
                      </span>
                    )}
                  </Link>
                </li>
              ))
            ) : (
              <li className="text-sm text-slate-500">No posts yet</li>
            )}
          </ul>
        </div>
      );

    default:
      return null;
  }
}

// Sidebar Container
function Sidebar({ sidebar, posts, categories }) {
  const navigate = useNavigate();
  
  if (!sidebar || !sidebar.settings?.enabled || !sidebar.widgets?.length) {
    return null;
  }

  const widthClasses = {
    sm: 'w-[250px]',
    md: 'w-[300px]',
    lg: 'w-[350px]'
  };

  const handleSearch = (query) => {
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query)}`);
    }
  };

  return (
    <aside 
      className={`${widthClasses[sidebar.settings.width] || 'w-[300px]'} flex-shrink-0 hidden lg:block`}
      data-testid="page-sidebar"
    >
      <div className="sticky top-24 space-y-6">
        {sidebar.widgets.map((widget) => (
          <div 
            key={widget.id} 
            className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-slate-100 dark:border-slate-700"
          >
            <RenderSidebarWidget 
              widget={widget} 
              posts={posts} 
              categories={categories}
              onSearch={handleSearch}
            />
          </div>
        ))}
      </div>
    </aside>
  );
}

export default function DynamicPage() {
  const { slug } = useParams();
  const [content, setContent] = useState(null);
  const [contentType, setContentType] = useState(null); // 'page' or 'post'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState(null);
  const [sidebar, setSidebar] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  // Fetch site settings (for AdSense)
  useEffect(() => {
    fetchSettings();
    fetchSidebar();
    fetchRecentPosts();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings`);
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        
        // Inject Google Analytics
        if (data.google_analytics_id && !document.querySelector('script[src*="googletagmanager"]')) {
          const script = document.createElement('script');
          script.async = true;
          script.src = `https://www.googletagmanager.com/gtag/js?id=${data.google_analytics_id}`;
          document.head.appendChild(script);
          
          const initScript = document.createElement('script');
          initScript.textContent = `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${data.google_analytics_id}');
          `;
          document.head.appendChild(initScript);
        }
        
        // Inject Google Search Console verification meta tag
        if (data.google_search_console_verification && !document.querySelector('meta[name="google-site-verification"]')) {
          const meta = document.createElement('meta');
          meta.name = 'google-site-verification';
          meta.content = data.google_search_console_verification;
          document.head.appendChild(meta);
        }
        
        // Set global AdSense publisher ID
        if (data.google_adsense_publisher_id) {
          window.ADSENSE_PUBLISHER_ID = data.google_adsense_publisher_id;
          
          // Load AdSense script if not already loaded
          if (!document.querySelector('script[src*="adsbygoogle"]')) {
            const script = document.createElement('script');
            script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${data.google_adsense_publisher_id}`;
            script.async = true;
            script.crossOrigin = 'anonymous';
            document.head.appendChild(script);
          }
        }
      }
    } catch (error) {
      console.log('Failed to load settings');
    }
  };

  const fetchSidebar = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sidebar`);
      if (response.ok) {
        const data = await response.json();
        setSidebar(data);
      }
    } catch (error) {
      console.log('Failed to load sidebar');
    }
  };

  const fetchRecentPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/posts?status=published&limit=10`);
      if (response.ok) {
        const data = await response.json();
        setRecentPosts(data);
        
        // Extract unique categories from posts
        const uniqueCategories = [...new Set(data.filter(p => p.category).map(p => p.category))];
        const catObjects = uniqueCategories.map(cat => ({
          name: cat,
          slug: cat.toLowerCase().replace(/\s+/g, '-'),
          count: data.filter(p => p.category === cat).length
        }));
        setCategories(catObjects);
      }
    } catch (error) {
      console.log('Failed to load posts');
    }
  };

  // Initialize ads after content loads
  useEffect(() => {
    if (content && settings?.google_adsense_publisher_id) {
      // Push ads after a small delay to ensure DOM is ready
      setTimeout(() => {
        try {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        } catch (e) {
          console.log('AdSense error:', e);
        }
      }, 100);
    }
  }, [content, settings]);

  // Helper function to update SEO meta tags in document head
  const updateSEOTags = (seo, fallbackTitle) => {
    // Update document title
    document.title = seo?.meta_title || fallbackTitle || 'KayTool';
    
    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', seo?.meta_description || '');
    
    // Update or create meta keywords
    if (seo?.meta_keywords) {
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', seo.meta_keywords);
    }
    
    // Update Open Graph tags
    if (seo?.og_title) {
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (!ogTitle) {
        ogTitle = document.createElement('meta');
        ogTitle.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitle);
      }
      ogTitle.setAttribute('content', seo.og_title);
    }
    
    if (seo?.og_description) {
      let ogDescription = document.querySelector('meta[property="og:description"]');
      if (!ogDescription) {
        ogDescription = document.createElement('meta');
        ogDescription.setAttribute('property', 'og:description');
        document.head.appendChild(ogDescription);
      }
      ogDescription.setAttribute('content', seo.og_description);
    }
    
    if (seo?.og_image) {
      let ogImage = document.querySelector('meta[property="og:image"]');
      if (!ogImage) {
        ogImage = document.createElement('meta');
        ogImage.setAttribute('property', 'og:image');
        document.head.appendChild(ogImage);
      }
      ogImage.setAttribute('content', seo.og_image);
    }
    
    // Update Twitter card
    let twitterCard = document.querySelector('meta[name="twitter:card"]');
    if (!twitterCard) {
      twitterCard = document.createElement('meta');
      twitterCard.setAttribute('name', 'twitter:card');
      document.head.appendChild(twitterCard);
    }
    twitterCard.setAttribute('content', seo?.twitter_card || 'summary_large_image');
    
    // Update canonical URL
    if (seo?.canonical_url) {
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', seo.canonical_url);
    }
    
    // Update robots meta (noindex/nofollow)
    let robots = document.querySelector('meta[name="robots"]');
    if (seo?.no_index || seo?.no_follow) {
      if (!robots) {
        robots = document.createElement('meta');
        robots.setAttribute('name', 'robots');
        document.head.appendChild(robots);
      }
      const robotsContent = [];
      if (seo.no_index) robotsContent.push('noindex');
      if (seo.no_follow) robotsContent.push('nofollow');
      robots.setAttribute('content', robotsContent.join(', '));
    } else if (robots) {
      robots.remove();
    }
  };

  useEffect(() => {
    fetchContent();
  }, [slug]);

  const fetchContent = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // First try to find as a page
      let response = await fetch(`${API_URL}/api/pages/slug/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setContent(data);
        setContentType('page');
        updateSEOTags(data.seo, data.title);
        setLoading(false);
        return;
      }

      // If not a page, try as a post
      response = await fetch(`${API_URL}/api/posts/slug/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setContent(data);
        setContentType('post');
        updateSEOTags(data.seo, data.title);
        setLoading(false);
        return;
      }

      // Neither found
      setError('Page not found');
    } catch (err) {
      setError('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-6xl font-bold text-slate-300 dark:text-slate-700 mb-4">404</h1>
          <p className="text-xl text-slate-600 dark:text-slate-400 mb-6">{error}</p>
          <Link to="/" className="inline-block px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-full font-medium">
            Go back home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors duration-300">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-800/70 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <a href="https://kaytool.com" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900 dark:text-white">KayTool</span>
            </a>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setDarkMode(!darkMode)}
              className="rounded-full w-10 h-10 border-slate-200 dark:border-slate-600 dark:bg-slate-700"
            >
              {darkMode ? <Sun className="w-5 h-5 text-yellow-500" /> : <Moon className="w-5 h-5 text-slate-600" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-12">
        {/* Determine if sidebar should show based on page/post-specific settings */}
        {(() => {
          // Check page/post specific sidebar settings
          const pageSidebar = content?.sidebar || { mode: 'global' };
          
          // If mode is 'none', don't show sidebar at all
          if (pageSidebar.mode === 'none') {
            return (
              <article className="max-w-4xl mx-auto" data-testid="page-content">
                {renderPageContent()}
              </article>
            );
          }
          
          // If mode is 'custom', use page-specific widgets
          if (pageSidebar.mode === 'custom' && pageSidebar.widgets?.length > 0) {
            const customSidebarSettings = {
              widgets: pageSidebar.widgets,
              settings: {
                enabled: true,
                position: pageSidebar.position || 'right',
                width: pageSidebar.width || 'md',
                showOnPages: true,
                showOnPosts: true
              }
            };
            const sidebarPosition = pageSidebar.position || 'right';
            
            return (
              <div className={`flex gap-8 ${sidebarPosition === 'left' ? 'flex-row-reverse' : ''}`}>
                <article className="flex-1 min-w-0 max-w-4xl" data-testid="page-content">
                  {renderPageContent()}
                </article>
                <Sidebar sidebar={customSidebarSettings} posts={recentPosts} categories={categories} />
              </div>
            );
          }
          
          // Default: use global sidebar (mode === 'global')
          // Check if global sidebar should show
          const globalShowSidebar = sidebar?.settings?.enabled && sidebar?.widgets?.length > 0 && (
            (contentType === 'page' && sidebar?.settings?.showOnPages) ||
            (contentType === 'post' && sidebar?.settings?.showOnPosts)
          );
          
          // Apply page-specific overrides to global sidebar if any
          const effectiveSidebar = globalShowSidebar ? {
            ...sidebar,
            settings: {
              ...sidebar.settings,
              position: pageSidebar.position || sidebar.settings.position,
              width: pageSidebar.width || sidebar.settings.width
            }
          } : null;
          
          const sidebarPosition = effectiveSidebar?.settings?.position || 'right';
          
          if (effectiveSidebar) {
            return (
              <div className={`flex gap-8 ${sidebarPosition === 'left' ? 'flex-row-reverse' : ''}`}>
                <article className="flex-1 min-w-0 max-w-4xl" data-testid="page-content">
                  {renderPageContent()}
                </article>
                <Sidebar sidebar={effectiveSidebar} posts={recentPosts} categories={categories} />
              </div>
            );
          }
          
          // No sidebar - original layout
          return (
            <article className="max-w-4xl mx-auto" data-testid="page-content">
              {renderPageContent()}
            </article>
          );
          
          // Helper function to render page content
          function renderPageContent() {
            return (
              <>
                {/* Post-specific header with featured image */}
                {contentType === 'post' && content?.featured_image && (
                  <img src={content.featured_image} alt={content.title} className="w-full rounded-xl mb-8" />
                )}
                
                {/* Post metadata */}
                {contentType === 'post' && (
                  <div className="flex items-center gap-4 mb-6">
                    {content?.category && (
                      <Badge className="bg-blue-600 text-white">
                        <Tag className="w-3 h-3 mr-1" />
                        {content.category}
                      </Badge>
                    )}
                    {content?.created_at && (
                      <span className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(content.created_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                )}

                {/* Title */}
                <h1 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-8">
                  {content?.title}
                </h1>
                
                {/* Post excerpt */}
                {contentType === 'post' && content?.excerpt && (
                  <p className="text-xl text-slate-600 dark:text-slate-400 mb-8 leading-relaxed">
                    {content.excerpt}
                  </p>
                )}

                {/* Components */}
                {content?.components?.map((component, index) => (
                  <RenderComponent key={component.id || index} component={component} />
                ))}
              </>
            );
          }
        })()}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-800/70 mt-16">
        <div className="container mx-auto px-4 py-6 text-center">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Powered by KayTool
          </p>
        </div>
      </footer>
    </div>
  );
}
